<script setup lang="ts">
import './assets/style.scss'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { InventTypeField } from '@/entities/Inventory/TypeField'
import { useTranslate } from '@/shared/lib'
import { useLoadSubTypesList } from '@/entities/Inventory/ObjectSubTypes/model/api'
import {
  isObjectPassportCatalogField,
  isObjectPassportPlacementPseudoDisplay,
  isObjectPassportSelectableField,
  isObjectPassportTableField,
  objectPassportFieldDefinitionToInventoryField,
  objectPassportFieldPlacement,
  objectPassportPlacementDisplayText,
  objectPassportValueToEditScalar,
  objectPassportValueToRuntimeScalar,
} from '../model/objectPassportFieldAdapter'
import { findObjectSubTypeOption, type ObjectSubTypeOption } from '@/entities/Inventory/ObjectSubTypes/model/helpers'
import type { ObjectPassportFieldDefinition, ObjectPassportFieldValue } from '../model/types'

export type PseudosqlOverrides = {
  value_result: string | null
  error: string | null
}

const props = withDefaults(
  defineProps<{
    definition: ObjectPassportFieldDefinition
    /** Значение из `data[]` (для display-полей и ошибок pseudo_sql). */
    value?: ObjectPassportFieldValue | null
    /** Id размещения (`field_props.id`) для placement-специфичного display/table. */
    placementLayoutId?: number | null
    /** Явно включает/выключает table-слот независимо от других размещений поля. */
    forceTableView?: boolean | null
    label?: string
    variant?: string
    density?: string
    hideDetails?: boolean
    readonly?: boolean
    disabled?: boolean
    loading?: boolean
    showMandatoryError?: boolean
    /** Клик по readonly-значению (например, открыть модалку справочника). */
    clickable?: boolean
    /** Переопределение `value_result`/`error` размещения после пересчёта pseudosql. */
    pseudosqlOverrides?: PseudosqlOverrides | null
    /** Подпись выбранного значения из модалки (пока options селекта ещё не подтянулись). */
    selectionLabel?: string | null
  }>(),
  {
    value: null,
    placementLayoutId: null,
    forceTableView: null,
    hideDetails: true,
    readonly: false,
    disabled: false,
    loading: false,
    showMandatoryError: false,
    clickable: false,
    pseudosqlOverrides: null,
    selectionLabel: null,
  },
)

const emit = defineEmits<{
  openSelect: []
}>()

const { trs } = useTranslate()
const model = defineModel<string>({ default: '' })
const route = useRoute()

const groupIdForCatalog = computed(() => String(route.params.object_id ?? ''))
const fieldCatalogId = computed(() => {
  if (!isObjectPassportCatalogField(props.definition)) return 0
  return props.definition.catalog_id ?? 0
})

const {
  data: catalogSubtypesData,
  isPending: catalogSubtypesPending,
} = useLoadSubTypesList(groupIdForCatalog, fieldCatalogId)

const catalogSubtypes = computed(() => {
  const raw = catalogSubtypesData.value?.data?.sub_types
  return Array.isArray(raw) ? (raw as ObjectSubTypeOption[]) : []
})

const inventoryField = computed(() => objectPassportFieldDefinitionToInventoryField(props.definition))

/** Метаданные селекта с подставленной опцией текущего model (имя из модалки / API). */
const selectableInventoryField = computed(() => {
  const base = inventoryField.value
  const rawId = model.value == null ? '' : String(model.value).trim()
  if (!rawId) return base
  const items = Array.isArray(base.items) ? [...base.items] : []
  if (items.some((item) => String(item.id) === rawId)) return base
  const name =
    props.selectionLabel?.trim() ||
    (props.value ? objectPassportValueToRuntimeScalar(props.value).trim() : '') ||
    rawId
  const numericId = Number(rawId)
  items.push({
    id: Number.isFinite(numericId) ? numericId : (rawId as unknown as number),
    name,
  })
  return { ...base, items }
})

const placement = computed(() =>
  objectPassportFieldPlacement(props.definition, props.placementLayoutId),
)

const isTableField = computed(() => {
  if (props.forceTableView === true) return true
  if (props.forceTableView === false) return false
  return isObjectPassportTableField(props.definition)
})

const isDisplayField = computed(() =>
  isObjectPassportPlacementPseudoDisplay(
    placement.value,
    props.definition,
    props.clickable,
  ),
)

const isSelectableField = computed(() => isObjectPassportSelectableField(props.definition))

const needsCatalogValidation = computed(
  () => isSelectableField.value && isDisplayField.value && isObjectPassportCatalogField(props.definition),
)

const isInvalidCatalogDefault = computed(() => {
  if (!needsCatalogValidation.value) return false
  if (catalogSubtypesPending.value) return false
  const rawId = model.value == null ? '' : String(model.value).trim()
  if (!rawId) return false
  const matched = findObjectSubTypeOption(catalogSubtypes.value, rawId)
  return matched == null
})

const displayText = computed(() => {
  if (needsCatalogValidation.value && catalogSubtypesPending.value) return ''
  if (isInvalidCatalogDefault.value) return ''
  if (props.pseudosqlOverrides?.value_result?.trim()) {
    return props.pseudosqlOverrides.value_result
  }
  if (props.selectionLabel?.trim()) {
    return props.selectionLabel.trim()
  }
  const modelStr = model.value == null ? '' : String(model.value).trim()
  const apiEdit = props.value ? objectPassportValueToEditScalar(props.value).trim() : ''
  const apiDisplay = props.value ? objectPassportValueToRuntimeScalar(props.value).trim() : ''

  // После выбора в модалке model уже новый, а props.value ещё со старого GET.
  if (modelStr && modelStr !== apiEdit) {
    return modelStr
  }
  if (isDisplayField.value) {
    const placed = objectPassportPlacementDisplayText(
      props.definition,
      props.placementLayoutId,
      props.value,
    ).trim()
    if (placed) return placed
  }
  return apiDisplay || modelStr
})

/**
 * Подпись поля: переданный label (уже со *), иначе имя + * по is_required.
 */
const fieldLabel = computed(() => {
  const fromProp = props.label?.trim()
  if (fromProp) return fromProp
  const base = props.definition.name?.trim() || ''
  if (props.definition.is_required && base) return `${base} *`
  return base
})

const showReadonlyDisplay = computed(
  () =>
    props.readonly &&
    (isDisplayField.value || (isSelectableField.value && props.clickable)),
)

// Вычисляемое поле (pseudo_sql), недоступное для выбора: простой текст без рамки.
// Кликабельные pseudo_sql-поля остаются в showReadonlyDisplay (выбор через модалку).
const showDisplayText = computed(
  () => props.readonly && isDisplayField.value && !props.clickable,
)

const showSelectableReadonlyInput = computed(() => {
  if (!isSelectableField.value || !props.clickable || isDisplayField.value) return false
  const type = String(inventoryField.value.type ?? '')
  // Связи с объектами: в гриде только текст, выбор — в модалке (ObjectLinkSelect не поддерживает plain).
  if (type === 'link_object' || type === 'object_link') return false
  return true
})

const showMandatoryHint = computed(() => Boolean(props.showMandatoryError))

const mandatoryErrorMessage = computed(() =>
  showMandatoryHint.value ? trs('validate.required') : undefined,
)

const fieldVariant = computed(() => props.variant || 'outlined')
const fieldDensity = computed(() => props.density || 'comfortable')

function onReadonlyValueClick() {
  if (!props.clickable || props.disabled) return
  emit('openSelect')
}
</script>

<template>
  <div
    class="object-passport-field"
    :class="{
      'object-passport-field--display': isDisplayField,
      'object-passport-field--table': isTableField,
      'object-passport-field--selectable-readonly': isSelectableField && readonly && clickable,
      'object-passport-field--mandatory-error': showMandatoryHint,
    }"
  >
    <slot v-if="isTableField" name="table" :definition="definition" :value="value" />

    <!-- Вычисляемое поле (pseudo_sql), недоступное для выбора: простой текст без рамки. -->
    <div v-else-if="showDisplayText" class="object-passport-field__display-text">
      <div class="object-passport-field__display-text-label">{{ fieldLabel }}</div>
      <div class="object-passport-field__display-text-value">{{ displayText || '—' }}</div>
      <div
        v-if="pseudosqlOverrides?.error || placement?.error || value?.error"
        class="object-passport-field__error"
      >
        {{ pseudosqlOverrides?.error || placement?.error || value?.error }}
      </div>
    </div>

    <!--
      Тот же outlined + floating label, что у скаляров — иначе ячейки справочников
      с label сверху съезжают относительно соседних колонок грида.
    -->
    <div
      v-else-if="showReadonlyDisplay"
      class="object-passport-field__outlined-readonly"
      :class="{
        'object-passport-field__outlined-readonly--clickable': clickable && !disabled,
      }"
      :role="clickable && !disabled ? 'button' : undefined"
      :tabindex="clickable && !disabled ? 0 : undefined"
      @click="onReadonlyValueClick"
      @keydown.enter.prevent="onReadonlyValueClick"
      @keydown.space.prevent="onReadonlyValueClick"
    >
      <InventTypeField
        v-if="showSelectableReadonlyInput"
        :model-value="model"
        class="object-passport-field__selectable-readonly-input"
        :type="String(selectableInventoryField.type)"
        :field-data="selectableInventoryField"
        :label="fieldLabel"
        :variant="fieldVariant"
        :density="fieldDensity"
        :hide-details="hideDetails && !showMandatoryHint"
        readonly
        :disabled="disabled"
        :loading="loading"
        :show-mandatory-error="showMandatoryError"
      />
      <VTextField
        v-else
        class="object-passport-field__display-text-input"
        :model-value="displayText || '—'"
        :label="fieldLabel"
        :variant="fieldVariant"
        :density="fieldDensity"
        :hide-details="hideDetails && !showMandatoryHint"
        :error="showMandatoryHint ? true : undefined"
        :error-messages="showMandatoryHint ? mandatoryErrorMessage : undefined"
        :append-inner-icon="clickable && !disabled ? 'keyboard_arrow_down' : undefined"
        readonly
        :disabled="disabled"
        :loading="loading"
      />
      <div
        v-if="
          !showMandatoryHint &&
          (pseudosqlOverrides?.error || placement?.error || value?.error)
        "
        class="object-passport-field__error"
      >
        {{ pseudosqlOverrides?.error || placement?.error || value?.error }}
      </div>
    </div>

    <InventTypeField
      v-else
      v-model="model"
      class="object-passport-field__input"
      :type="String(inventoryField.type)"
      :field-data="inventoryField"
      :label="fieldLabel"
      :variant="fieldVariant"
      :density="fieldDensity"
      :hide-details="hideDetails && !showMandatoryHint"
      :readonly="readonly"
      :disabled="disabled"
      :loading="loading"
      :show-mandatory-error="showMandatoryError"
    />

    <div
      v-if="!isTableField && !readonly && value?.error && !isDisplayField"
      class="object-passport-field__error"
    >
      {{ value.error }}
    </div>
  </div>
</template>
